import { IssueType, Severity, useCurrentWorkspace, useCustomQuery } from '@gripp/shared-logic';
import { TimeRange } from '../types';

/**
 * Shape of a single activity point returned by `homeActivityMap`.
 * Mirrors the generated `HomeActivityPoint` from the backend codegen — verified
 * against `backend-workspace/apps/gripp-api/generated/index.ts` schema-first.
 * Coordinates arrive as separate lat/lng (source order [lng,lat] in Mongo;
 * resolver already splits them).
 */
export interface HomeActivityPoint {
  id: string;
  lat: number;
  lng: number;
  severity: Severity;
  type: IssueType;
  title: string;
  createdAt: string;
}

/**
 * Converts a `TimeRange` to `{ startDate, endDate }` ISO-8601 strings
 * (inclusive, matching the resolver's `resolveRange` helper).
 * Returns `undefined` for both when range is 'all' — the resolver returns all
 * workspace history when both dates are absent.
 *
 * All arithmetic uses UTC setters (`setUTCHours` / `setUTCDate` /
 * `setUTCFullYear`). The backend buckets in UTC (`floorToGranularity` /
 * `advanceByGranularity` in `homeDashboardUtils.ts`), so computing the window
 * in local time would shift it by the viewer's offset and, around DST
 * transitions, drift the day/year boundaries off by an hour relative to the
 * bucket axis. UTC keeps the queried window stable and aligned with bucketing.
 */
export function rangeToDates(range: TimeRange): { startDate?: string; endDate?: string } {
  const now = new Date();
  const end = now.toISOString();

  switch (range) {
    case 'today': {
      const start = new Date(now);
      start.setUTCHours(0, 0, 0, 0);
      return { startDate: start.toISOString(), endDate: end };
    }
    case '7d': {
      const start = new Date(now);
      start.setUTCDate(start.getUTCDate() - 7);
      return { startDate: start.toISOString(), endDate: end };
    }
    case '30d': {
      const start = new Date(now);
      start.setUTCDate(start.getUTCDate() - 30);
      return { startDate: start.toISOString(), endDate: end };
    }
    case '90d': {
      const start = new Date(now);
      start.setUTCDate(start.getUTCDate() - 90);
      return { startDate: start.toISOString(), endDate: end };
    }
    case '1y': {
      const start = new Date(now);
      start.setUTCFullYear(start.getUTCFullYear() - 1);
      return { startDate: start.toISOString(), endDate: end };
    }
    case 'all':
      return {};
    default:
      return {};
  }
}

const HOME_ACTIVITY_MAP_QUERY = `
  query HomeActivityMap($filter: HomeActivityFilter) {
    homeActivityMap(filter: $filter) {
      id
      lat
      lng
      severity
      type
      title
      createdAt
    }
  }
`;

/**
 * Fetches located activity points for the map, scoped to the current workspace
 * and the selected time range.
 *
 * Root query — reads `data.homeActivityMap` directly (no unwrap). Guards
 * `currentWorkspace?.id` before issuing the query (undefined on first render).
 *
 * Point volume: raw points are returned as-is per the Phase 1 design decision.
 * If a busy workspace over 'All' returns an unacceptable volume, add
 * server-side clustering or a capped limit later and log() the cap — never
 * silently truncate here.
 */
export function useHomeActivityMap(range: TimeRange): {
  data: HomeActivityPoint[] | undefined;
  isLoading: boolean;
  error: Error | null;
} {
  const workspace = useCurrentWorkspace();
  const workspaceId = workspace?.id;
  const { startDate, endDate } = rangeToDates(range);

  const { data, isLoading, error } = useCustomQuery({
    cacheKey: ['homeActivityMap', workspaceId, range],
    query: HOME_ACTIVITY_MAP_QUERY,
    variables: {
      filter: {
        // Scope to the current (possibly impersonated) workspace; the query is
        // gated on workspaceId below so it is always present (GRIPP #2859).
        workspaceId,
        ...(startDate ? { startDate } : {}),
        ...(endDate ? { endDate } : {}),
      },
    },
    options: {
      // Do not issue the query until the workspace id is known.
      enabled: !!workspaceId,
      staleTime: 60_000,
    },
  });

  const points: HomeActivityPoint[] | undefined = data?.homeActivityMap;

  return { data: points, isLoading, error: error as Error | null };
}
